import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  makeStyles
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: { flex: 1, padding: theme.spacing(1), overflowY: "auto" },
  color: { width: 56, height: 20, borderRadius: 4, display: "inline-block" }
}));

const Tags = () => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#3B82F6");

  const load = async () => {
    try {
      const { data } = await api.get("/tags");
      setTags(data);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => { load(); }, []);

  const close = () => {
    setOpen(false);
    setEditing(null);
    setName("");
    setColor("#3B82F6");
  };

  const edit = tag => {
    setEditing(tag);
    setName(tag.name);
    setColor(tag.color);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/tags/${editing.id}`, { name, color });
      else await api.post("/tags", { name, color });
      toast.success(i18n.t("tags.toasts.saved"));
      close();
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async tag => {
    if (!window.confirm(i18n.t("tags.confirmDelete"))) return;
    try {
      await api.delete(`/tags/${tag.id}`);
      setTags(current => current.filter(item => item.id !== tag.id));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("tags.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            {i18n.t("tags.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{i18n.t("tags.table.name")}</TableCell>
            <TableCell align="center">{i18n.t("tags.table.color")}</TableCell>
            <TableCell align="right">{i18n.t("tags.table.actions")}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {tags.map(tag => <TableRow key={tag.id}>
              <TableCell>{tag.name}</TableCell>
              <TableCell align="center"><span className={classes.color} style={{ backgroundColor: tag.color }} /></TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => edit(tag)}><Edit /></IconButton>
                <IconButton size="small" onClick={() => remove(tag)}><DeleteOutline /></IconButton>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </Paper>
      <Dialog open={open} onClose={close}>
        <DialogTitle>{editing ? i18n.t("tags.modal.edit") : i18n.t("tags.modal.add")}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" variant="outlined" label={i18n.t("tags.table.name")} value={name} onChange={event => setName(event.target.value)} />
          <TextField fullWidth margin="dense" variant="outlined" type="color" label={i18n.t("tags.table.color")} value={color} onChange={event => setColor(event.target.value)} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{i18n.t("tags.buttons.cancel")}</Button>
          <Button color="primary" variant="contained" disabled={!name.trim()} onClick={save}>{i18n.t("tags.buttons.save")}</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Tags;
